'use client'

import { useState, useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useTranslation } from 'react-i18next'
import { ExternalLink, CheckCircle2, Clock, XCircle, Zap, Info } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Toaster as SonnerToaster } from '@/components/ui/sonner'
import { trpc } from '@/lib/api/trpc'
import { toast } from 'sonner'

// ============================================================================
// Types
// ============================================================================

interface WhopPlatformsFormProps {
    defaultValues?: Record<string, any>
    onSubmit: (data: Record<string, any>) => Promise<void>
    isLoading?: boolean
    storeId?: string
}

// ============================================================================
// Component
// ============================================================================

export function WhopPlatformsForm({
    isLoading = false,
    storeId,
}: WhopPlatformsFormProps) {
    const { t } = useTranslation('integrations')
    const { user } = useUser()
    const accountEmail = user?.primaryEmailAddress?.emailAddress ?? ''
    const [contactEmail, setContactEmail] = useState(accountEmail)

    // Sync once Clerk resolves the user (initial render may have empty accountEmail)
    useEffect(() => {
        if (accountEmail && !contactEmail) setContactEmail(accountEmail)
    }, [accountEmail])

    const { data: status, isLoading: isLoadingStatus, refetch: refetchStatus } =
        trpc.integrations.getWhopPlatformsStatus.useQuery(
            { storeId: storeId! },
            { enabled: !!storeId, refetchOnWindowFocus: false },
        )

    const connectMutation = trpc.integrations.initiateWhopPlatformsConnect.useMutation({
        onSuccess: (data) => {
            // Redirect to Whop's KYC onboarding page
            window.location.href = data.redirectUrl
        },
        onError: (error) => {
            toast.error(error.message || t('whopPlatforms.toasts.initiateFailed'))
        },
    })

    const disconnectMutation = trpc.integrations.disconnectWhopPlatforms.useMutation({
        onSuccess: () => {
            toast.success(t('whopPlatforms.toasts.disconnected'))
            refetchStatus()
        },
        onError: (error) => {
            toast.error(error.message || t('whopPlatforms.toasts.disconnectFailed'))
        },
    })

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)

    const handleConnect = () => {
        if (!storeId || !isValidEmail) return
        connectMutation.mutate({ storeId, email: contactEmail })
    }

    const handleDisconnect = () => {
        if (!storeId) return
        disconnectMutation.mutate({ storeId })
    }

    const connectionState = status?.connectionState ?? null
    const whopCompanyId = status?.whopCompanyId ?? null

    const isConnecting = connectMutation.isPending
    const isDisconnecting = disconnectMutation.isPending

    return (
        <div className="space-y-6">
            {/* Sonner's toast() calls are silently dropped without a mounted
                <Toaster />, and the app-wide one only wires up the separate
                shadcn/useToast system — not sonner. Scoped here rather than
                globally until the other sonner call sites across the app
                are audited. */}
            <SonnerToaster />

            {/* Connection status */}
            {isLoadingStatus ? (
                <div className="text-muted-foreground text-sm">{t('whopPlatforms.checkingConnection')}</div>
            ) : (
                <>
                    {/* Not connected */}
                    {!connectionState || connectionState === 'disconnected' ? (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Zap className="h-5 w-5 text-violet-500" />
                                    {t('whopPlatforms.notConnectedTitle')}
                                </CardTitle>
                                <CardDescription>
                                    {t('whopPlatforms.notConnectedDescription')}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="whop-contact-email">{t('whopPlatforms.contactEmail')}</Label>
                                    <Input
                                        id="whop-contact-email"
                                        type="email"
                                        value={contactEmail}
                                        onChange={(e) => setContactEmail(e.target.value)}
                                        placeholder="you@example.com"
                                    />
                                </div>
                                <Alert>
                                    <Info className="h-4 w-4" />
                                    <AlertTitle>{t('whopPlatforms.emailNoticeTitle')}</AlertTitle>
                                    <AlertDescription>
                                        {t('whopPlatforms.emailNotice')}
                                    </AlertDescription>
                                </Alert>
                                <Button
                                    onClick={handleConnect}
                                    disabled={isConnecting || isLoading || !isValidEmail}
                                    className="bg-violet-600 hover:bg-violet-700"
                                >
                                    <ExternalLink className="mr-2 h-4 w-4" />
                                    {isConnecting ? t('whopPlatforms.connecting') : t('whopPlatforms.connect')}
                                </Button>
                            </CardContent>
                        </Card>
                    ) : null}

                    {/* Pending KYC */}
                    {connectionState === 'pending_kyc' ? (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Clock className="h-5 w-5 text-amber-500" />
                                    {t('whopPlatforms.pendingKycTitle')}
                                </CardTitle>
                                <CardDescription>
                                    {t('whopPlatforms.pendingKycDescription')}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {whopCompanyId && (
                                    <div>
                                        <p className="text-muted-foreground text-xs">{t('whopPlatforms.whopCompanyId')}</p>
                                        <p className="font-mono text-sm">{whopCompanyId}</p>
                                    </div>
                                )}
                                <div className="flex gap-2">
                                    <Button onClick={handleConnect} variant="outline" disabled={isConnecting || isLoading}>
                                        <ExternalLink className="mr-2 h-4 w-4" />
                                        {isConnecting ? t('whopPlatforms.redirecting') : t('whopPlatforms.continueKyc')}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        onClick={handleDisconnect}
                                        disabled={isDisconnecting}
                                        className="text-destructive hover:text-destructive"
                                    >
                                        {t('whopPlatforms.disconnect')}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ) : null}

                    {/* Pending review — submitted, awaiting Whop's decision */}
                    {connectionState === 'pending_review' ? (
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <Clock className="h-5 w-5 text-amber-500" />
                                    {t('whopPlatforms.pendingReviewTitle')}
                                </CardTitle>
                                <CardDescription>
                                    {t('whopPlatforms.pendingReviewDescription')}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {whopCompanyId && (
                                    <div>
                                        <p className="text-muted-foreground text-xs">{t('whopPlatforms.whopCompanyId')}</p>
                                        <p className="font-mono text-sm">{whopCompanyId}</p>
                                    </div>
                                )}
                                <div className="flex justify-end">
                                    <Button
                                        variant="ghost"
                                        onClick={handleDisconnect}
                                        disabled={isDisconnecting}
                                        className="text-destructive hover:text-destructive"
                                    >
                                        {t('whopPlatforms.disconnect')}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ) : null}

                    {/* Rejected / action required — Whop needs a retry or more info */}
                    {connectionState === 'rejected' || connectionState === 'action_required' ? (
                        <Card className={connectionState === 'rejected' ? 'border-destructive/50' : 'border-amber-300'}>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    {connectionState === 'rejected' ? (
                                        <XCircle className="h-5 w-5 text-destructive" />
                                    ) : (
                                        <Info className="h-5 w-5 text-amber-500" />
                                    )}
                                    {connectionState === 'rejected'
                                        ? t('whopPlatforms.rejectedTitle')
                                        : t('whopPlatforms.actionRequiredTitle')}
                                </CardTitle>
                                <CardDescription>
                                    {connectionState === 'rejected'
                                        ? t('whopPlatforms.rejectedDescription')
                                        : t('whopPlatforms.actionRequiredDescription')}
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {status?.lastVerificationMessage && (
                                    <Alert variant={connectionState === 'rejected' ? 'destructive' : 'default'}>
                                        <AlertDescription>{status.lastVerificationMessage}</AlertDescription>
                                    </Alert>
                                )}
                                <div className="flex gap-2">
                                    <Button onClick={handleConnect} variant="outline" disabled={isConnecting || isLoading}>
                                        <ExternalLink className="mr-2 h-4 w-4" />
                                        {isConnecting ? t('whopPlatforms.redirecting') : t('whopPlatforms.retryKyc')}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        onClick={handleDisconnect}
                                        disabled={isDisconnecting}
                                        className="text-destructive hover:text-destructive"
                                    >
                                        {t('whopPlatforms.disconnect')}
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ) : null}

                    {/* Connected */}
                    {connectionState === 'connected' ? (
                        <>
                            <Alert className="border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30">
                                <CheckCircle2 className="h-4 w-4 text-green-600" />
                                <AlertTitle className="text-green-700 dark:text-green-400">{t('whopPlatforms.connectedTitle')}</AlertTitle>
                                <AlertDescription className="text-green-600 dark:text-green-500">
                                    {t('whopPlatforms.connectedDescription')}
                                    {whopCompanyId && (
                                        <span className="ml-1 font-mono text-xs">({whopCompanyId})</span>
                                    )}
                                </AlertDescription>
                            </Alert>

                            <div className="flex justify-end">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={handleDisconnect}
                                    disabled={isDisconnecting}
                                    className="text-destructive hover:text-destructive"
                                >
                                    <XCircle className="mr-2 h-4 w-4" />
                                    {isDisconnecting ? t('whopPlatforms.disconnecting') : t('whopPlatforms.disconnect')}
                                </Button>
                            </div>
                        </>
                    ) : null}
                </>
            )}

            {/* Webhook info */}
            <Card className="bg-muted/40">
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">{t('whopPlatforms.webhookTitle')}</CardTitle>
                </CardHeader>
                <CardContent>
                    <p className="font-mono text-xs break-all select-all">
                        {window.location.protocol + '//' + window.location.hostname.replace(/^crm\./, 'api.')}/api/public/webhooks/whop-platforms
                    </p>
                    <p className="text-muted-foreground mt-1 text-xs">
                        {t('whopPlatforms.webhookHint')}
                    </p>
                </CardContent>
            </Card>
        </div>
    )
}
